// Eigene Klangbearbeitung: alle Stufen an/aus mit Reglern, Live-Messung (LUFS, Gain-Reduction je Stufe,
// Spektrum vorher/nachher), A/B-Vergleich (Original · Eigene · StereoTool · LSP) und Presets mit Import/Export.
// Änderungen gelten im laufenden Sendeweg (ohne Unterbrechung).

import { useEffect, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad, usePoll } from "../../api";
import { Field, Switch, useMe } from "../../ui";

const DEFAULTS: Record<string, any> = {
  input_gain_db: 0, declipper: true, declip_level: 0.985, dc_filter: true, subsonic_hz: 25, auto_mono: true, phase_rotator: true, phase_rotator_hz: 200,
  agc: true, agc_target_db: -18, agc_max_gain_db: 10, agc_speed_db_s: 1, agc2: true, agc2_range_db: 5, agc2_speed_db_s: 8, clipper_oversample: true,
  gate: true, gate_threshold_db: -50, gate_ratio: 2,
  eq: true, peq_type: [4, 2, 1, 1, 1, 1, 1, 3], peq_hz: [30, 100, 250, 500, 1200, 3000, 6000, 10000], peq_db: [0, 0, 0, 0, 0, 0, 0, 0], peq_q: [0.707, 0.707, 1, 1, 1, 1, 1, 0.707],
  deesser: true, deesser_hz: 6500, deesser_threshold_db: -28, deesser_max_db: 6,
  multiband: true, mb_crossovers: [120, 400, 2000, 6000], mb_threshold_db: [-22, -20, -19, -20, -22], mb_ratio: [3, 2.5, 2.5, 2.5, 3],
  mb_attack_ms: [35, 20, 12, 6, 4], mb_release_ms: [350, 250, 180, 130, 100], mb_gain_db: [2, 1, 1, 1.5, 2], mb_knee_db: 6,
  mb_agc: true, mb_agc_range_db: 4, auto_eq: false, auto_eq_range_db: 4, auto_eq_tilt_db: -1.5, mb_clip: false, mb_clip_db: -3,
  bass_mono: true, bass_mono_hz: 120, bass_boost_db: 0, bass_boost_hz: 80, subharmonic_db: -60, bass_tight: 0,
  stereo_width: 1, enhancer: 0, width_limit: 0.9, exciter: 0, exciter_hz: 4000,
  loudness: true, loudness_target_lufs: -14, loudness_range_db: 6,
  clipper: true, clipper_drive_db: 1, limiter_ceiling_db: -1, limiter_release_ms: 80, codec_headroom_db: 0.5,
};
const EQ_TYPES: [number, string][] = [[0, "aus"], [1, "Glocke"], [2, "Bass-Shelf"], [3, "Höhen-Shelf"], [4, "Hochpass"], [5, "Tiefpass"]];

/** Older settings have 4 bands and the legacy EQ: bring them to the current form. */
function normalise(own: any) {
  const v = { ...DEFAULTS, ...own };
  if (!Array.isArray(own?.peq_type) && own && ("eq_mid1_db" in own || "eq_low_shelf_db" in own)) {
    v.peq_type = [4, 2, 1, 1, 3];
    v.peq_hz = [own.eq_highpass_hz ?? 30, own.eq_low_shelf_hz ?? 100, own.eq_mid1_hz ?? 300, own.eq_mid2_hz ?? 3000, own.eq_high_shelf_hz ?? 8000];
    v.peq_db = [0, own.eq_low_shelf_db ?? 0, own.eq_mid1_db ?? 0, own.eq_mid2_db ?? 0, own.eq_high_shelf_db ?? 0];
    v.peq_q = [0.707, 0.707, own.eq_mid1_q ?? 1, own.eq_mid2_q ?? 1, 0.707];
  }
  for (const k of ["peq_type", "peq_hz", "peq_db", "peq_q"]) while (v[k].length < 8) v[k] = [...v[k], k === "peq_type" ? 0 : k === "peq_q" ? 1 : k === "peq_hz" ? 1000 : 0];
  return fitBands(v, v.mb_crossovers.length + 1);
}

/** Resize per-band arrays to n bands (new bands copy their neighbour). */
function fitBands(v: any, n: number) {
  const out = { ...v };
  if (out.mb_crossovers.length !== n - 1) {
    out.mb_crossovers = Array.from({ length: n - 1 }, (_, i) => Math.round(80 * Math.pow(8000 / 80, (i + 1) / n) / 10) * 10);
  }
  for (const k of ["mb_threshold_db", "mb_ratio", "mb_attack_ms", "mb_release_ms", "mb_gain_db"]) {
    const a: number[] = Array.isArray(out[k]) ? out[k] : [];
    out[k] = Array.from({ length: n }, (_, i) => a[Math.round((i / Math.max(1, n - 1)) * (a.length - 1))] ?? DEFAULTS[k][0]);
  }
  return out;
}

/** Horizontal gain-reduction bar (0 … -12 dB). */
function GR({ db, label, max = 12, color = "var(--warn)" }: { db: number | undefined; label: string; max?: number; color?: string }) {
  const v = Math.min(max, Math.max(0, Math.abs(db ?? 0)));
  return (
    <div className="row small" style={{ gap: 6 }}>
      <span style={{ width: 96 }}>{label}</span>
      <div className="meter" style={{ flex: 1 }}><div style={{ width: `${(v / max) * 100}%`, background: color }} /></div>
      <span className="mono" style={{ width: 52, textAlign: "right" }}>{(db ?? 0).toFixed(1)}</span>
    </div>
  );
}

function Slider({ label, k, v, set, min, max, step, unit, idx, fmt }: { label: string; k: string; v: any; set: (k: string, val: any, idx?: number) => void; min: number; max: number; step: number; unit: string; idx?: number; fmt?: (x: number) => string }) {
  const cur = idx === undefined ? v[k] : v[k][idx];
  return (
    <Field label={`${label}: ${fmt ? fmt(cur) : cur}${unit}`}>
      <input type="range" min={min} max={max} step={step} value={cur} onChange={(e) => set(k, Number(e.target.value), idx)} />
    </Field>
  );
}

/** Spectrum before (grey) and after (accent), 32 log bands 30 Hz … 16 kHz. */
function SpectrumView({ a, b }: { a?: number[]; b?: number[] }) {
  const w = 520, h = 140;
  const line = (v?: number[]) => (v?.length ? v.map((d, i) => `${(i / (v.length - 1)) * w},${h - ((Math.max(-90, Math.min(0, d)) + 90) / 90) * h}`).join(" ") : "");
  return (
    <svg viewBox={`0 0 ${w} ${h + 14}`} style={{ width: "100%", height: 170, background: "#10241d", borderRadius: 8 }}>
      {[100, 1000, 10000].map((f) => {
        const x = (Math.log(f / 30) / Math.log(16000 / 30)) * w;
        return <g key={f}><line x1={x} x2={x} y1={0} y2={h} stroke="#24352e" /><text x={x + 2} y={h + 11} fontSize={9} fill="#7f978c">{f >= 1000 ? `${f / 1000} k` : f}</text></g>;
      })}
      <polyline points={line(a)} fill="none" stroke="#8aa298" strokeWidth={1.5} />
      <polyline points={line(b)} fill="none" stroke="#e8673c" strokeWidth={2} />
      <text x={6} y={12} fontSize={10} fill="#8aa298">vorher</text>
      <text x={50} y={12} fontSize={10} fill="#e8673c">nachher</text>
    </svg>
  );
}

/** A/B: render an excerpt through each processor and switch while listening (same position). */
function AbCompare({ v }: { v: any }) {
  const { data: lspPresets } = useLoad<any[]>("/api/processor/lsp-presets");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<any[]>([]);
  const [song, setSong] = useState<any | null>(null);
  const [from, setFrom] = useState(60);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [cur, setCur] = useState<string | null>(null);
  const [lspPreset, setLspPreset] = useState("balanced");
  const audio = useRef<HTMLAudioElement>(new Audio());
  useEffect(() => {
    if (!q.trim()) return setHits([]);
    const t = window.setTimeout(async () => setHits((await api(`/api/library?q=${encodeURIComponent(q)}&kind=music`)).items.slice(0, 8)), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  useEffect(() => () => audio.current.pause(), []);
  const play = async (variant: string, preset?: string) => {
    if (!song) return;
    const cacheKey = variant === "lsp" ? `lsp:${preset || "balanced"}` : variant;
    let url = urls[cacheKey];
    if (!url) {
      setBusy(cacheKey);
      try {
        const r = await api("/api/processor/preview", { body: { media_id: song.id, from_ms: from * 1000, seconds: 25, variant, preset, own: variant === "own" ? v : undefined } });
        url = r.url;
        setUrls((u) => ({ ...u, [cacheKey]: url }));
        if (r.status?.after) setStats((x) => ({ ...x, [cacheKey]: r.status.after, raw: x.raw ?? r.status.before }));
      } catch (e: any) {
        toast(e.message, true);
        return;
      } finally {
        setBusy(null);
      }
    }
    const a = audio.current;
    const pos = cur ? a.currentTime : 0;
    a.src = url;
    a.addEventListener("loadedmetadata", () => { a.currentTime = Math.min(pos, Math.max(0, a.duration - 0.5)); a.play().catch(() => {}); }, { once: true });
    setCur(cacheKey);
  };
  // Settings changed: the "own" excerpt must be rendered again.
  useEffect(() => { setUrls((u) => { const { own: _, ...rest } = u; void _; return rest; }); }, [JSON.stringify(v)]);
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>A/B-Vergleich (nicht auf Sendung)</h3>
      <div className="row wrap">
        <input type="text" placeholder="Song suchen …" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} />
        {song && <b className="small">{song.artist} – {song.title}</b>}
        <span className="small">ab</span><input type="number" min={0} style={{ width: 70 }} value={from} onChange={(e) => { setFrom(Number(e.target.value)); setUrls({}); }} /><span className="small">s</span>
      </div>
      {hits.length > 0 && !song && <div className="small" style={{ marginTop: 4 }}>{hits.map((h) => <div key={h.id} style={{ cursor: "pointer", padding: "2px 0" }} onClick={() => { setSong(h); setQ(""); setHits([]); setUrls({}); setFrom(Math.min(60, Math.round((h.duration_ms ?? 0) / 3000))); }}>{h.artist} – {h.title} <span className="muted">{fmtMs(h.duration_ms)}</span></div>)}</div>}
      <div className="row wrap" style={{ marginTop: 8, gap: 6 }}>
        {[["raw", "Original"], ["own", "Eigene Bearbeitung"], ["stereotool", "StereoTool"]].map(([k, l]) => (
          <button key={k} className={`btn small${cur === k ? " primary" : ""}`} disabled={!song || busy !== null} onClick={() => play(k)}>{busy === k ? "rechne …" : `▶ ${l}`}</button>
        ))}
        <select aria-label="LSP-Vergleichspreset" value={lspPreset} onChange={(e) => setLspPreset(e.target.value)} style={{ maxWidth: 300 }}>
          {(lspPresets ?? []).map((x: any) => <option key={x.id} value={x.id}>{x.name}{x.source !== "Radiocast" ? ` · ${x.source}` : ""}</option>)}
        </select>
        <button className={`btn small${cur === `lsp:${lspPreset}` ? " primary" : ""}`} disabled={!song || busy !== null} onClick={() => play("lsp", lspPreset)}>{busy === `lsp:${lspPreset}` ? "rechne …" : "▶ LSP vorhören"}</button>
        <button className="btn small" onClick={() => { audio.current.pause(); setCur(null); }}>■</button>
        {song && <button className="btn small" onClick={() => { setSong(null); setUrls({}); setStats({}); }}>anderer Song</button>}
      </div>
      {Object.keys(stats).length > 0 && (
        <table className="list small" style={{ marginTop: 8, maxWidth: 520 }}>
          <thead><tr><th /><th>Lautheit (LUFS)</th><th>True Peak (dBTP)</th><th>Crest (dB)</th></tr></thead>
          <tbody>
            {[["raw", "Original"], ["own", "Eigene Bearbeitung"], ["stereotool", "StereoTool"], [`lsp:${lspPreset}`, `LSP · ${lspPresets?.find((x: any) => x.id === lspPreset)?.name ?? "Preset"}`]].filter(([k]) => stats[k]).map(([k, l]) => (
              <tr key={k}><td>{l}</td><td className="mono">{stats[k].lufs.toFixed(1)}</td><td className="mono">{stats[k].true_peak.toFixed(1)}</td><td className="mono">{stats[k].crest.toFixed(1)}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="small muted" style={{ marginBottom: 0 }}>Beim Umschalten läuft die Stelle weiter – so hörst du direkt den Unterschied. Gerechnet wird in einem eigenen Prozess, der Sender merkt davon nichts.</p>
    </div>
  );
}

export function OwnPanel({ own, onSaved }: { own: any; onSaved: () => void }) {
  const me = useMe();
  const can = me?.role === "admin";
  const [v, setV] = useState<any>(normalise(own));
  const { data: presets, reload: reloadPresets } = useLoad<any[]>("/api/processor/own-presets");
  const { data: live } = usePoll<any>("/api/status", 1000);
  const m = live?.engine?.processor?.meters;
  const timer = useRef<number | undefined>(undefined);
  const file = useRef<HTMLInputElement>(null);
  useEffect(() => setV(normalise(own)), [own]);

  const push = (next: any) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        await api("/api/processor", { method: "PUT", body: { own: next } });
      } catch (e: any) {
        toast(e.message, true);
      }
    }, 350);
  };
  const set = (k: string, val: any, idx?: number) => {
    if (!can) return;
    const next = { ...v, [k]: idx === undefined ? val : v[k].map((x: number, i: number) => (i === idx ? val : x)) };
    setV(next);
    push(next);
  };
  const setBands = (n: number) => { const next = fitBands(v, n); setV(next); push(next); };
  const stage = (title: string, key: string | null, children: React.ReactNode, gr?: React.ReactNode) => (
    <div className="card" style={{ padding: 12, opacity: key && !v[key] ? 0.6 : 1 }}>
      <div className="row" style={{ marginBottom: 6 }}>
        <b>{title}</b><span className="spacer" />
        {key && <Switch checked={!!v[key]} onChange={(x) => set(key, x)} />}
      </div>
      {gr}
      <div className="form">{children}</div>
    </div>
  );
  const nb = v.mb_crossovers.length + 1;
  const bandName = (i: number) => `${i === 0 ? 20 : v.mb_crossovers[i - 1]}–${i === nb - 1 ? "20k" : v.mb_crossovers[i]} Hz`;
  const importPreset = async (f: File) => {
    try {
      const j = JSON.parse(await f.text());
      await api("/api/processor/own-presets/import", { body: { name: j.name ?? f.name.replace(/\.json$/, ""), params: j.params ?? j } });
      toast("Preset importiert");
      reloadPresets();
    } catch (e: any) {
      toast(e.message ?? "Datei nicht lesbar", true);
    }
  };
  const lufsColor = (x?: number) => (x == null ? undefined : Math.abs(x - v.loudness_target_lufs) <= 1 ? "var(--ok)" : "var(--warn)");
  return (
    <div className="stack">
      <div className="card">
        <div className="row wrap">
          <h2 style={{ margin: 0 }}>Eigene Klangbearbeitung</h2>
          <span className="spacer" />
          <span className="small muted">Preset:</span>
          {(presets ?? []).map((p) => (
            <span key={p.id} className="row" style={{ gap: 2 }}>
              <button className="btn small" disabled={!can} onClick={async () => { await api(`/api/processor/own-presets/${p.id}/apply`, { method: "POST" }); toast(`Preset „${p.name}“ aktiv`); onSaved(); }}>{p.name}</button>
              <a className="iconbtn small" title="Exportieren" href={`/api/processor/own-presets/${p.id}/export`}>⇩</a>
              {!p.builtin && can && <button className="iconbtn small" title="Löschen" onClick={async () => { if (window.confirm(`Preset „${p.name}“ löschen?`)) { await api(`/api/processor/own-presets/${p.id}`, { method: "DELETE" }); reloadPresets(); } }}>✕</button>}
            </span>
          ))}
          {can && <button className="btn small" onClick={async () => { const n = window.prompt("Name für das Preset (aktuelle Einstellungen)"); if (n) { await api("/api/processor/own-presets", { body: { name: n } }); reloadPresets(); toast("Preset gespeichert"); } }}>+ Speichern</button>}
          {can && <><button className="btn small" onClick={() => file.current?.click()}>⇪ Importieren</button><input ref={file} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void importPreset(f); e.target.value = ""; }} /></>}
        </div>
        <div className="grid g4" style={{ marginTop: 12 }}>
          <div className="kpi"><span className="v" style={{ color: lufsColor(m?.lufs_s) }}>{m?.lufs_s != null ? m.lufs_s.toFixed(1) : "–"}</span><span className="l">LUFS kurz (Ziel {v.loudness_target_lufs})</span></div>
          <div className="kpi"><span className="v">{m?.lufs_m != null ? m.lufs_m.toFixed(1) : "–"} / {m?.lufs_i != null ? m.lufs_i.toFixed(1) : "–"}</span><span className="l">LUFS momentan / integriert</span></div>
          <div className="kpi"><span className="v">{m ? m.limiter_gr_db.toFixed(1) : "–"}</span><span className="l">Limiter dB</span></div>
          <div className="kpi"><span className="v">{m ? m.output_peak_db.toFixed(1) : "–"}</span><span className="l">Ausgang Spitze (True Peak)</span></div>
        </div>
        <div className="row small muted wrap" style={{ marginTop: 6, gap: 12 }}>
          <span>Eingang {m ? m.input_db.toFixed(0) : "–"} dBFS</span>
          <span>Verzögerung {m ? Math.round(m.latency_ms) : "–"} ms</span>
          <span>Korrelation {m ? m.correlation.toFixed(2) : "–"}</span>
          {m?.mono_fix && <span className="badge warn">Mono-Automatik aktiv (ein Kanal fehlt)</span>}
          {m && m.declipped > 0 && <span>Declipper: {m.declipped} Stellen repariert</span>}
        </div>
        <SpectrumView a={m?.spectrum_in} b={m?.spectrum_out} />
        <p className="small muted" style={{ marginBottom: 0 }}>Regler wirken sofort im laufenden Sendeweg. Der Limiter am Ende ist immer aktiv; über die Obergrenze (abzüglich Codec-Reserve) kommt nichts hinaus.</p>
      </div>
      <AbCompare v={v} />
      <div className="grid g2">
        {stage("Eingang", null, <>
          <Slider label="Eingangspegel" k="input_gain_db" v={v} set={set} min={-12} max={12} step={0.5} unit=" dB" />
          <Field label="Declipper (repariert übersteuerte Spitzen)"><Switch checked={!!v.declipper} onChange={(x) => set("declipper", x)} label={`ab ${Math.round(v.declip_level * 100)} % Vollaussteuerung`} /></Field>
          <Slider label="Declipper-Schwelle" k="declip_level" v={v} set={set} min={0.9} max={1} step={0.005} unit="" fmt={(x) => `${(x * 100).toFixed(1)} %`} />
          <Field label="DC-/Subsonic-Filter"><Switch checked={!!v.dc_filter} onChange={(x) => set("dc_filter", x)} label={`unter ${v.subsonic_hz} Hz`} /></Field>
          <Slider label="Subsonic bis" k="subsonic_hz" v={v} set={set} min={10} max={60} step={1} unit=" Hz" />
          <Field label="Mono-Automatik"><Switch checked={!!v.auto_mono} onChange={(x) => set("auto_mono", x)} label="fehlenden Kanal ergänzen" /></Field>
          <Field label="Phasen-Rotator (symmetrische Sprache)"><Switch checked={!!v.phase_rotator} onChange={(x) => set("phase_rotator", x)} label={`${v.phase_rotator_hz} Hz`} /></Field>
        </>)}
        {stage("AGC (zweistufig)", "agc", <>
          <Slider label="Ziel" k="agc_target_db" v={v} set={set} min={-30} max={-10} step={0.5} unit=" dB" />
          <Slider label="Max. Anhebung/Absenkung" k="agc_max_gain_db" v={v} set={set} min={0} max={20} step={1} unit=" dB" />
          <Slider label="Geschwindigkeit" k="agc_speed_db_s" v={v} set={set} min={0.1} max={5} step={0.1} unit=" dB/s" />
          <Field label="Zweite, schnelle Stufe"><Switch checked={!!v.agc2} onChange={(x) => set("agc2", x)} label={`± ${v.agc2_range_db} dB, ${v.agc2_speed_db_s} dB/s`} /></Field>
          <Slider label="Schnelle Stufe: Bereich" k="agc2_range_db" v={v} set={set} min={0} max={12} step={0.5} unit=" dB" />
          <Slider label="Schnelle Stufe: Tempo" k="agc2_speed_db_s" v={v} set={set} min={1} max={20} step={1} unit=" dB/s" />
        </>, <><GR db={m?.agc_gain_db} label="langsam" color="var(--accent)" /><GR db={m?.agc2_gain_db} label="schnell" color="var(--accent)" /></>)}
        {stage("Expander / Gate", "gate", <>
          <Slider label="Schwelle" k="gate_threshold_db" v={v} set={set} min={-80} max={-20} step={1} unit=" dB" />
          <Slider label="Verhältnis" k="gate_ratio" v={v} set={set} min={1} max={6} step={0.5} unit=":1" />
        </>, <GR db={m?.gate_gr_db} label="Absenkung" />)}
        {stage("Parametrik-EQ (8 Bänder)", "eq", <>
          <div className="field full">
            <table className="list small"><thead><tr><th>Typ</th><th>Hz</th><th>dB</th><th>Q</th></tr></thead><tbody>
              {Array.from({ length: 8 }, (_, i) => (
                <tr key={i}>
                  <td><select value={v.peq_type[i]} onChange={(e) => set("peq_type", Number(e.target.value), i)}>{EQ_TYPES.map(([t, l]) => <option key={t} value={t}>{l}</option>)}</select></td>
                  <td><input type="number" min={20} max={20000} value={v.peq_hz[i]} onChange={(e) => set("peq_hz", Number(e.target.value), i)} style={{ width: 80 }} /></td>
                  <td><input type="number" min={-15} max={15} step={0.5} value={v.peq_db[i]} disabled={[0, 4, 5].includes(v.peq_type[i])} onChange={(e) => set("peq_db", Number(e.target.value), i)} style={{ width: 64 }} /></td>
                  <td><input type="number" min={0.1} max={10} step={0.1} value={v.peq_q[i]} onChange={(e) => set("peq_q", Number(e.target.value), i)} style={{ width: 60 }} /></td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </>)}
        {stage("De-Esser", "deesser", <>
          <Slider label="Frequenz" k="deesser_hz" v={v} set={set} min={3000} max={10000} step={100} unit=" Hz" />
          <Slider label="Schwelle" k="deesser_threshold_db" v={v} set={set} min={-50} max={-10} step={1} unit=" dB" />
          <Slider label="Max. Absenkung" k="deesser_max_db" v={v} set={set} min={0} max={12} step={0.5} unit=" dB" />
        </>, <GR db={m?.deesser_gr_db} label="Absenkung" />)}
        {stage(`Mehrband (${nb} Bänder): AGC, Kompressor, Auto-EQ, Clipper`, "multiband", <>
          <Field label="Anzahl Bänder"><select value={nb} disabled={!can} onChange={(e) => setBands(Number(e.target.value))}>{[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
          <Slider label="Knie" k="mb_knee_db" v={v} set={set} min={0} max={12} step={1} unit=" dB" />
          <div className="field full">
            <table className="list small"><thead><tr><th>Band</th><th>bis Hz</th><th>Schwelle</th><th>Verh.</th><th>Attack ms</th><th>Release ms</th><th>Pegel</th></tr></thead><tbody>
              {Array.from({ length: nb }, (_, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{i < nb - 1 ? <input type="number" min={40} max={16000} value={v.mb_crossovers[i]} onChange={(e) => set("mb_crossovers", Number(e.target.value), i)} style={{ width: 70 }} /> : "–"}</td>
                  {(["mb_threshold_db", "mb_ratio", "mb_attack_ms", "mb_release_ms", "mb_gain_db"] as const).map((k) => (
                    <td key={k}><input type="number" step={k === "mb_ratio" ? 0.5 : 1} value={v[k][i]} onChange={(e) => set(k, Number(e.target.value), i)} style={{ width: 58 }} /></td>
                  ))}
                </tr>
              ))}
            </tbody></table>
          </div>
          <Field label="Mehrband-AGC"><Switch checked={!!v.mb_agc} onChange={(x) => set("mb_agc", x)} label={`± ${v.mb_agc_range_db} dB`} /></Field>
          <Field label="Auto-EQ (spektrale Balance)"><Switch checked={!!v.auto_eq} onChange={(x) => set("auto_eq", x)} label={`± ${v.auto_eq_range_db} dB, Neigung ${v.auto_eq_tilt_db} dB/Band`} /></Field>
          <Slider label="Auto-EQ Neigung" k="auto_eq_tilt_db" v={v} set={set} min={-4} max={2} step={0.5} unit=" dB/Band" />
          <Field label="Band-Clipper"><Switch checked={!!v.mb_clip} onChange={(x) => set("mb_clip", x)} label={`bei ${v.mb_clip_db} dB`} /></Field>
          <Slider label="Band-Clipper Schwelle" k="mb_clip_db" v={v} set={set} min={-12} max={0} step={0.5} unit=" dB" />
        </>, <>{Array.from({ length: nb }, (_, i) => <GR key={i} db={m?.band_gr_db?.[i]} label={bandName(i)} />)}
          {v.auto_eq && <div className="small muted">Auto-EQ: {(m?.band_eq_db ?? []).map((x: number) => (x >= 0 ? "+" : "") + x.toFixed(1)).join(" · ")} dB</div>}</>)}
        {stage("Bass", null, <>
          <Field label="Bass-Mono"><Switch checked={!!v.bass_mono} onChange={(x) => set("bass_mono", x)} label={`unter ${v.bass_mono_hz} Hz`} /></Field>
          <Slider label="Bass-Mono bis" k="bass_mono_hz" v={v} set={set} min={60} max={250} step={5} unit=" Hz" />
          <Slider label="Bass-Verstärkung" k="bass_boost_db" v={v} set={set} min={0} max={8} step={0.5} unit=" dB" />
          <Slider label="Subharmonik (Oktave tiefer)" k="subharmonic_db" v={v} set={set} min={-60} max={-6} step={1} unit=" dB" fmt={(x) => (x <= -60 ? "aus" : String(x))} />
          <Slider label="Bass straffen" k="bass_tight" v={v} set={set} min={0} max={1} step={0.05} unit="" />
        </>)}
        {stage("Stereo & Höhen", null, <>
          <Slider label="Stereo-Breite" k="stereo_width" v={v} set={set} min={0} max={2} step={0.05} unit="×" />
          <Slider label="Stereo-Enhancer" k="enhancer" v={v} set={set} min={0} max={1} step={0.05} unit="" />
          <Slider label="Breitenbegrenzung (Seite/Mitte)" k="width_limit" v={v} set={set} min={0.2} max={2} step={0.05} unit="" />
          <Slider label="Exciter (Obertöne)" k="exciter" v={v} set={set} min={0} max={1} step={0.05} unit="" />
          <Slider label="Exciter ab" k="exciter_hz" v={v} set={set} min={1500} max={10000} step={100} unit=" Hz" />
        </>)}
        {stage("Lautheit (LUFS-Ziel)", "loudness", <>
          <Slider label="Ziel" k="loudness_target_lufs" v={v} set={set} min={-24} max={-8} step={0.5} unit=" LUFS" />
          <Slider label="Regelbereich" k="loudness_range_db" v={v} set={set} min={0} max={12} step={0.5} unit=" dB" />
        </>, <GR db={m?.loudness_gain_db} label="Korrektur" color="var(--accent)" />)}
        {stage("Clipper & True-Peak-Limiter", "clipper", <>
          <Slider label="Clipper-Antrieb" k="clipper_drive_db" v={v} set={set} min={0} max={6} step={0.5} unit=" dB" />
          <Field label="Clipper mit 4× Überabtastung"><Switch checked={!!v.clipper_oversample} onChange={(x) => set("clipper_oversample", x)} label={v.clipper_oversample ? "an (ohne Aliasing)" : "aus"} /></Field>
          <Slider label="Obergrenze (True Peak)" k="limiter_ceiling_db" v={v} set={set} min={-3} max={-0.1} step={0.1} unit=" dB" />
          <Slider label="Codec-Reserve (MP3/AAC)" k="codec_headroom_db" v={v} set={set} min={0} max={2} step={0.1} unit=" dB" />
          <Slider label="Limiter-Rückstellzeit" k="limiter_release_ms" v={v} set={set} min={20} max={500} step={10} unit=" ms" />
        </>, <><GR db={m?.clipper_db} label="Clipper" /><GR db={m?.limiter_gr_db} label="Limiter" /></>)}
      </div>
    </div>
  );
}
